import { H2 } from '../reusable-components';
import NavBar from '../navbar/navbar';
import { useTranslation } from 'react-i18next';
import useShallowRounds from '../rounds/useShallowRounds';
import 'moment/locale/sv';
import 'moment/locale/fi';
import 'moment/locale/en-gb';
import 'moment/locale/fa';
import 'moment/locale/nb';
import 'moment/locale/de';
import 'moment/locale/fr';
import 'moment/locale/ar';
import 'moment/locale/es';
import 'moment/locale/tr';
import 'moment/locale/pl';
import 'moment/locale/da';
import { getProPickSuggestions } from "../common/RoundHelper";
import Progress from "../common/progress";
import { Box, Grid, SxProps, Theme, Typography } from "@mui/material";
import { usePlayer } from '../player/playerContext';
import RobotPlateSlim from './robotplateslim';
import RoundBoxWithButton from './roundboxwithbutton';
import { GetProPickBetsLabel, IsLeo, IsVideo, themeC } from '../theme/themehelper';
import SyndicateBanner from './syndicatebanner';
import SyndicateList from '../syndicates/syndicatelist';
import useSyndicates from '../syndicates/useSyndicates';
import LandingInfoBox from './landinginfobox';
import FreebetListItem from '../freebet/freebetlistitem';
import useFreebets from '../syndicates/useFreebets';
import { ShallowRound } from '../models/round';
import { Syndicate } from '../models/syndicate';
import RoundBoxSettledContent from './roundboxsettledcontent';
import TheGameTile from './thegametile';
import { getTheGameManifest, isTheGameRoundName } from '../roundview/components/thegame/thegamemanifest';

const LandingPage = () => {

  const { t } = useTranslation();

  let [shallowRoundsResponse, de] = useShallowRounds(true, false, 0, 30);
  let [settledRoundsResponse] = useShallowRounds(false, true, 0, 45);
  let playerResp: any = usePlayer();
  const [syndicates = []] = useSyndicates();
  const freeBets = useFreebets();

  const agentSyndicateBetting = playerResp && playerResp[1] && playerResp[1].agentSyndicateBetting;
  let theme = window.theme;

  const styleBackground: SxProps<Theme> = {
    backgroundColor: 'var(--landing_background)',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: "-7.5px"
  };

  const styleSection: SxProps<Theme> = {
    margin: '10px 5px 0px 10px',
    flexWrap: 'wrap',
    justifyContent: 'center'
  };

  const activeRounds = shallowRoundsResponse && shallowRoundsResponse.rounds &&
    shallowRoundsResponse.rounds.filter(round => round.status === 'ACTIVE' && new Date(round.openTo) > new Date(Date.now()))
      .sort((a, b) => new Date(a.openTo).getTime() - new Date(b.openTo).getTime());

  let filteredSyndicates: Syndicate[] = [];

  let firstMax: ShallowRound[] = [];
  let firstMix: ShallowRound[] = [];
  let firstFlex: ShallowRound[] = [];
  let firstWinter: ShallowRound[] = [];
  let firstHockey: ShallowRound[] = [];

  if (activeRounds) {
    firstMax = activeRounds.filter(round => round.name.indexOf("Max") > -1);
    firstMix = activeRounds.filter(round => round.name.indexOf("Mix") > -1);
    firstFlex = activeRounds.filter(round => round.name.indexOf("Flex") > -1 || round.name.indexOf("Survivor") > -1);
    firstWinter = activeRounds.filter(round => round.name.indexOf("Winter") > -1 || round.name.indexOf("Hockey") > -1);
    firstHockey = activeRounds.filter(round => round.name.indexOf("Hockey") > -1);
    filteredSyndicates = syndicates.filter(({ roundId, agentName, nShares, targetNShares }) => activeRounds.filter(({ id }) => id === roundId.toString()).length > 0 &&
      agentName.toLowerCase() === 'anders svensson' && nShares < targetNShares);
  }

  let proPickMarginTop = "29px";
  if (IsLeo(window.theme)) {
    proPickMarginTop = "20px";
  }

  if (window.theme === themeC.Dbet) {
    if (filteredSyndicates.length > 0) {
      proPickMarginTop = "-20px";
    }
  }


  let topRounds: ShallowRound[] = []

  // Open and live The Game rounds; their names never match the Max/Mix/Flex filters above.
  const theGameRounds: ShallowRound[] = (shallowRoundsResponse?.rounds ?? [])
    .filter(round => round.status === 'ACTIVE' && (isTheGameRoundName(round.name) || getTheGameManifest(round) !== undefined))
    .sort((a, b) => new Date(a.openTo).getTime() - new Date(b.openTo).getTime());
  const openTheGameRounds = theGameRounds.filter(round => new Date(round.openTo) > new Date());
  const liveTheGameRounds = theGameRounds.filter(round => new Date(round.openTo) <= new Date());

  if (firstMax.length > 0) {
    topRounds.push(firstMax[0]);
  }

  if (firstMix.length > 0) {
    topRounds.push(firstMix[0]);
  }

  for (let i = 0; i < firstFlex.length && topRounds.length < 3; i++) {
    topRounds.push(firstFlex[i]);
  }

  const settledRounds: ShallowRound[] = [];
  {
    // Active rounds past their deadline (openTo has passed) — prefer these over settled
    const pastDeadlineRounds = shallowRoundsResponse && shallowRoundsResponse.rounds
      ? shallowRoundsResponse.rounds
          .filter(r => r.status === 'ACTIVE' && new Date(r.openTo) <= new Date(Date.now()))
          .sort((a, b) => new Date(b.openTo).getTime() - new Date(a.openTo).getTime())
      : [];

    const finishedRounds = settledRoundsResponse && settledRoundsResponse.rounds
      ? settledRoundsResponse.rounds
          .filter(r => r.status === 'FINISHED')
          .sort((a, b) => new Date(b.openTo).getTime() - new Date(a.openTo).getTime())
      : [];

    const findRound = (predicate: (r: ShallowRound) => boolean) =>
      pastDeadlineRounds.find(predicate) || finishedRounds.find(predicate);

    const lastMax = findRound(r => r.name.indexOf('Max') > -1);
    const lastMix = findRound(r => r.name.indexOf('Mix') > -1);
    const lastThird = findRound(r =>
      r.name.indexOf('Hockey') > -1 ||
      r.name.indexOf('Flex') > -1 ||
      r.name.indexOf('Survivor') > -1
    );

    if (lastMax) settledRounds.push(lastMax);
    if (lastMix) settledRounds.push(lastMix);
    if (lastThird) settledRounds.push(lastThird);
  }

  const renderTopRounds = (topRounds: ShallowRound[]) => {

    if (de.isLoading || de.isIdle) {
      return (
        <>
          <Progress />
        </>
      );
    }

    return topRounds.map((round, i) => {
      if (!round) {
        return <div />;
      }
      return (
        <RoundBoxWithButton key={`roundBox ${i}`} round={round} t={t} />
      );

    })
  }

  return (
    <>
      <NavBar whichToUnderline="0" balanceUpdateId="0" />

      <Grid container sx={styleBackground}>

        <LandingInfoBox theme={window.theme} />

        {freeBets && freeBets.length > 0 &&
          <>
            <Typography sx={{
              fontSize: "var(--font-title-size)",
              color: "var(--font-title-color)",
              lineHeight: "var(--font-title-size)",
              fontWeight: "var(--font-title-weight)",
              fontFamily: "var(--font-title)",
              marginTop: proPickMarginTop,
              marginBottom: '10px',
              textTransform: "var(--font-title-transform)"
            }}>{t('freebet')}</Typography>
            <Grid container sx={{ maxWidth: "900px", marginTop: "10px", marginLeft: "10px", marginRight: "10px", marginBottom: "10px", flexWrap: "wrap", justifyContent: "center" }}>
              {freeBets.map((bet, i) => {
                return (
                  <FreebetListItem freebet={bet} theme={theme} key={'free' + i} />
                );
              })
              }
            </Grid>
          </>
        }

        <Box sx={{ marginTop: "20px" }}>
          <H2>{t('current_rounds')}</H2>
        </Box>

        <Grid container sx={styleSection}>
          {renderTopRounds(topRounds)}
          {openTheGameRounds.map(round => (
            <TheGameTile key={`theGame_${round.id}`} round={round} />
          ))}
        </Grid>

        {IsVideo(theme) && firstWinter.length > 0 &&
          <Grid container sx={styleSection}>
            <RoundBoxWithButton key={`roundBox_winter`} round={firstWinter[0]} t={t} />
          </Grid>
        }

        {IsLeo(theme) && firstHockey.length > 0 &&
          <Grid container sx={styleSection}>
            <RoundBoxWithButton key={`roundBox_hockey`} round={firstHockey[0]} t={t} />
          </Grid>
        }

        {(settledRounds.length > 0 || liveTheGameRounds.length > 0) &&
          <>
            <Box sx={{ marginTop: "20px" }}>
              <H2>{t('results')}</H2>
            </Box>

            <Grid container sx={styleSection}>
              {liveTheGameRounds.map(round => (
                <TheGameTile key={`theGameLive_${round.id}`} round={round} />
              ))}
              {settledRounds.map((round, i) => (
                <RoundBoxWithButton key={`settledBox_${i}`} round={round} t={t}>
                  <RoundBoxSettledContent round={round} theme={theme} t={t} />
                </RoundBoxWithButton>
              ))}
            </Grid>
          </>
        }

        {((window.theme === themeC.Dbet) && agentSyndicateBetting) &&
          <SyndicateBanner theme={window.theme} />
        }

        {((window.theme === themeC.Dbet) && agentSyndicateBetting && filteredSyndicates.length > 0) &&
          <SyndicateList syndicates={filteredSyndicates} shallowRounds={activeRounds!} agentPage={false} theme={theme} />
        }        

        <Typography sx={{
          fontSize: "var(--font-title-size)",
          color: "var(--font-title-color)",
          lineHeight: "var(--font-title-size)",
          fontWeight: "var(--font-title-weight)",
          fontFamily: "var(--font-title)",
          marginTop: proPickMarginTop,
          marginBottom: '10px',
          textTransform: "var(--font-title-transform)"
        }}>{GetProPickBetsLabel(window.theme, t)}</Typography>

        <Grid container sx={{ maxWidth: "900px", marginTop: "10px", marginLeft: "10px", marginRight: "10px", marginBottom: "10px", flexWrap: "wrap", justifyContent: "center" }}>
          {topRounds.map((round, i) => {
            if (!round) {
              return <div></div>;
            }
            const suggestions = getProPickSuggestions(round);
            return suggestions.map((sugg, j) => {
              return (
                <RobotPlateSlim key={`proPickBox ${i}${j}`} round={round} t={t} sugg={sugg} />
              );
            })
          })
          }
        </Grid>

      </Grid>

    </>
  );
};

export default LandingPage;
